import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Brain,
  ArrowRight,
  CheckCircle,
  Sparkles,
} from "lucide-react";
import HeroSection from "../components/HeroSection";
import FeatureCards from "../components/FeatureCards";
import AutomationSection from "../components/AutomationSection";
import FooterNew from "../components/FooterNew";
import { coreServices, featuredProjects as projects } from "../data/mockData";

const Home = () => {
  return (
    <main className="bg-primary overflow-hidden">
      <HeroSection />
      <FeatureCards />

      {/* Core Services */}
      <section className="section-padding relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 tech-grid-dense opacity-25 pointer-events-none" />
        <div
          className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full opacity-[0.05] blur-[120px] pointer-events-none"
          style={{ background: "radial-gradient(circle, #A78BFA, transparent)" }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <span className="ai-badge mb-5 inline-flex">
              <Brain className="w-3.5 h-3.5 text-ai-glow" />
              Our Expertise
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5">
              Core <span className="gradient-text">Services</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
              We specialize in advanced AI technologies that drive business transformation
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-7">
            {coreServices.map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.5 }}
                whileHover={{ y: -6 }}
                className="rounded-2xl p-8 group cursor-pointer transition-all duration-400"
                style={{
                  background: "linear-gradient(135deg, rgba(15, 23, 42, 0.7), rgba(30, 41, 59, 0.5))",
                  backdropFilter: "blur(24px)",
                  border: "1px solid rgba(99, 102, 241, 0.06)",
                  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.02)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = `${service.color}25`;
                  e.currentTarget.style.boxShadow = `0 8px 40px rgba(0, 0, 0, 0.3), 0 0 25px ${service.color}10, inset 0 1px 0 rgba(255, 255, 255, 0.04)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(99, 102, 241, 0.06)";
                  e.currentTarget.style.boxShadow = "0 4px 24px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.02)";
                }}
              >
                <div
                  className="icon-glow w-14 h-14 rounded-xl flex items-center justify-center mb-7 transition-all duration-400 group-hover:scale-110"
                  style={{
                    background: `${service.color}10`,
                    border: `1px solid ${service.color}20`,
                    borderRadius: "14px",
                  }}
                >
                  <service.icon className="w-7 h-7" style={{ color: service.color, filter: `drop-shadow(0 0 8px ${service.color}50)` }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-5 group-hover:text-ai-glow transition-colors duration-300">
                  {service.title}
                </h3>
                <ul className="space-y-3.5 list-none p-0 m-0">
                  {service.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-[0.925rem] text-slate-400">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: service.color, filter: `drop-shadow(0 0 4px ${service.color}40)` }} />
                      {item}
                    </li>
                  ))}
                </ul>

                {/* Bottom glow line */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-all duration-700 rounded-b-2xl"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${service.color}, transparent)`,
                  }}
                />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link to="/services" className="neon-btn neon-btn-primary no-underline inline-flex items-center gap-2">
              View All Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>

        {/* Section divider */}
        <div className="section-divider mt-16" />
      </section>

      <AutomationSection />

      {/* Featured Projects */}
      <section className="section-padding relative overflow-hidden">
        {/* Background */}
        <div
          className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full opacity-[0.04] blur-[120px] pointer-events-none"
          style={{ background: "radial-gradient(circle, #6366F1, transparent)" }}
        />
        <div className="absolute inset-0 tech-grid opacity-20 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <span className="ai-badge mb-5 inline-flex">
              <Sparkles className="w-3.5 h-3.5 text-ai-glow" />
              Case Studies
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5">
              Featured <span className="gradient-text">Projects</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
              Explore some of our successful AI implementations across various industries
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-7">
            {projects.map((project, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.5 }}
                whileHover={{ y: -6, scale: 1.02 }}
                className="rounded-2xl overflow-hidden group cursor-pointer transition-all duration-400 relative"
                style={{
                  background: "linear-gradient(135deg, rgba(15, 23, 42, 0.7), rgba(30, 41, 59, 0.5))",
                  backdropFilter: "blur(24px)",
                  border: "1px solid rgba(99, 102, 241, 0.06)",
                  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.02)",
                }}
              >
                <div className="p-8">
                  <span
                    className="inline-block px-3.5 py-1 text-xs font-semibold rounded-full mb-5"
                    style={{
                      background: "rgba(99, 102, 241, 0.1)",
                      color: "#818CF8",
                      border: "1px solid rgba(99, 102, 241, 0.15)",
                    }}
                  >
                    {project.category}
                  </span>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-ai-glow transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-[0.925rem] text-slate-400 leading-relaxed">{project.desc}</p>
                </div>
                <div
                  className="h-[2px] w-0 group-hover:w-full transition-all duration-700"
                  style={{ background: "linear-gradient(90deg, #6366F1, #22D3EE)" }}
                />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link to="/projects" className="neon-btn neon-btn-outline no-underline inline-flex items-center gap-2">
              View All Projects
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <FooterNew />
    </main>
  );
};

export default Home;
